import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useNavigate, useParams } from 'react-router-dom';

import GradientCircularProgress from '../../components/common/GradientCircularProgress';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';
import { OrganizationType } from '../../types/organization';
import { OrganizationStaffMember } from '../../types/account';
import { OrganizationStaffRolesResponse, StaffSearchResult } from '../../types/api/organizationStaff';
import useBackendApi from '../../hooks/useBackendApi';
import { useGhostButtonProps } from '../../hooks/useGhostButtonProps';
import { RequestOptions } from '../../types/httpRequests';
import AddStaffModal from './components/AddStaffModal';
import BorderedButton from '../../components/common/buttons/BorderedButton';
import StaffTable from './components/StaffTable';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type RouteParams = {
  org_path_name: string;
};

type ActionMessage = {
  type: 'success' | 'error';
  text: string;
};

export const fetchOrganizationStaffData = async (
  orgPathName: string,
  apiFetchRequest: (req: RequestOptions) => Promise<any>,
) => {
  if (!orgPathName) return null;

  const options: RequestOptions = {
    endpoint: `${backendURL}/o/${encodeURIComponent(orgPathName)}/staff/`,
    method: 'GET',
  };

  const response = await apiFetchRequest(options);
  if (!response) return null;

  const body = response.body;
  const statusCode = body?.status_code ?? response.status;
  const message = body?.message;

  const unauthorized =
    statusCode === 401 || message === 'UNAUTHENTICATED' || message === 'UNAUTHORIZED' || message === 'NO_ACCESS';
  const notFound = statusCode === 404 || message === 'NOT_FOUND';

  return {
    status: response.status,
    body,
    isError: response.isError,
    unauthorized,
    notFound,
  };
};

export default function OrganizationStaff(): JSX.Element {
  const { theme } = useThemeContext();
  const { org_path_name } = useParams<RouteParams>();
  const navigate = useNavigate();

  const addStaffButtonProps = useGhostButtonProps({
    default: '/icons/user_gray550.svg',
    hover: '/icons/user_blue.svg',
  });

  const [organization, setOrganization] = useState<OrganizationType | null>(null);
  const [staff, setStaff] = useState<OrganizationStaffMember[]>([]);
  const [viewerOrgRoles, setViewerOrgRoles] = useState<string[]>([]);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchResults, setSearchResults] = useState<StaffSearchResult[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [isRolesLoading, setIsRolesLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<StaffSearchResult | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [isAddingStaff, setIsAddingStaff] = useState(false);
  const [dialogMessage, setDialogMessage] = useState<ActionMessage | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const invalidEmailText = 'This email is not allowed for this organization.';

  const canManageStaff = viewerOrgRoles.some((role) => role?.toLowerCase() === 'admin');
  const { apiFetchRequest, isLoading } = useBackendApi();
  const allowedDomains = (organization?.allowed_domains ?? []).map((domain) => domain?.toLowerCase()).filter(Boolean);

  const isEmailDomainAllowed = useCallback(
    (email?: string | null) => {
      if (!allowedDomains.length) return true;
      if (!email) return false;
      const parts = email.trim().toLowerCase().split('@');
      if (parts.length !== 2 || !parts[1]) return false;
      const domain = parts[1];
      return allowedDomains.includes(domain);
    },
    [allowedDomains],
  );

  const fail = useCallback(() => {
    setErrorText('Something went wrong. Please try again.');
    setViewerOrgRoles([]);
    setSearchResults([]);
    setHasSearched(false);
    setInputValue('');
    setDebouncedQuery('');
    setSearchError(null);
    setSelectedCandidate(null);
    setSelectedRole('');
    setRoles([]);
    setRolesError(null);
    setDialogMessage(null);
    setIsDialogOpen(false);
  }, []);

  const fetchOrganizationStaff = useCallback(async () => {
    if (!org_path_name) return;

    try {
      const result = await fetchOrganizationStaffData(org_path_name, apiFetchRequest);
      if (!result) return fail();

      const { body, isError, unauthorized, notFound } = result;

      if (unauthorized) {
        navigate('/401.html');
        return;
      }

      if (notFound) {
        navigate('/404.html');
        return;
      }

      if (isError || body?.message !== 'SUCCESS' || !body?.organization) {
        return fail();
      }

      setOrganization(body.organization as OrganizationType);
      setStaff(Array.isArray(body.staff) ? (body.staff as OrganizationStaffMember[]) : []);
      setViewerOrgRoles(Array.isArray(body.viewer_org_roles) ? body.viewer_org_roles : []);
      setErrorText(null);
      setSearchResults([]);
      setHasSearched(false);
      setInputValue('');
      setDebouncedQuery('');
      setSearchError(null);
      setSelectedCandidate(null);
      setSelectedRole('');
      setDialogMessage(null);
    } catch {
      fail();
    }
  }, [apiFetchRequest, fail, navigate, org_path_name]);

  const fetchAssignableRoles = useCallback(async () => {
    if (!org_path_name) return;

    setIsRolesLoading(true);
    setRolesError(null);

    try {
      const options: RequestOptions = {
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/staff/roles`,
        method: 'GET',
      };

      const response = await apiFetchRequest(options);
      if (!response) {
        setRolesError('Unable to load organization roles right now.');
        setRoles([]);
        setSelectedRole('');
        return;
      }

      const body = response.body as Partial<OrganizationStaffRolesResponse> & {
        status_code?: number;
        detail?: string;
        message?: string;
      };

      if (
        response.status === 401 ||
        body?.status_code === 401 ||
        body?.message === 'UNAUTHENTICATED' ||
        body?.message === 'NO_ACCESS'
      ) {
        navigate('/401.html');
        return;
      }

      if (response.status === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND') {
        navigate('/404.html');
        return;
      }

      if (response.isError || body?.message !== 'SUCCESS' || !Array.isArray(body.roles)) {
        const detail = (body?.detail as string) || 'Unable to load organization roles right now.';
        setRolesError(detail);
        setRoles([]);
        setSelectedRole('');
        return;
      }

      const normalizedRoles = body.roles
        .map((role) => role?.trim())
        .filter((role): role is string => Boolean(role))
        .map((role) => role.toLowerCase());
      setRoles(normalizedRoles);
      setSelectedRole(normalizedRoles[0] ?? '');
    } catch {
      setRolesError('Unable to load organization roles right now.');
      setRoles([]);
      setSelectedRole('');
    } finally {
      setIsRolesLoading(false);
    }
  }, [apiFetchRequest, navigate, org_path_name]);

  const handleOpenDialog = () => {
    setDialogMessage(null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (isAddingStaff) return;

    setIsDialogOpen(false);
    setInputValue('');
    setDebouncedQuery('');
    setSearchResults([]);
    setSearchError(null);
    setSelectedCandidate(null);
    setHasSearched(false);
    setDialogMessage(null);
    setRolesError(null);
    setRoles([]);
    setSelectedRole('');
    setIsInviting(false);
    abortRef.current?.abort();
  };

  useEffect(() => {
    if (!org_path_name) {
      navigate('/404.html');
      return;
    }

    setSearchResults([]);
    setHasSearched(false);
    setInputValue('');
    setDebouncedQuery('');
    setSearchError(null);
    setActionMessage(null);
    fetchOrganizationStaff();
  }, [fetchOrganizationStaff, navigate, org_path_name]);

  useEffect(() => {
    if (!isDialogOpen) return;

    setInputValue('');
    setDebouncedQuery('');
    setSearchResults([]);
    setSearchError(null);
    setSelectedCandidate(null);
    setDialogMessage(null);
    setHasSearched(false);
    setRolesError(null);
    setSelectedRole('');
    setRoles([]);
    abortRef.current?.abort();

    void fetchAssignableRoles();
  }, [fetchAssignableRoles, isDialogOpen]);

  useEffect(() => {
    if (!canManageStaff || !isDialogOpen) return;

    const trimmed = inputValue.trim().toLowerCase();

    if (!EMAIL_REGEX.test(trimmed)) {
      setDebouncedQuery('');
      setIsSearching(false);
      setSearchError(null);
      setSearchResults([]);
      setHasSearched(false);
      abortRef.current?.abort();
      return;
    }

    const handle = window.setTimeout(() => {
      setDebouncedQuery(trimmed);
    }, 650);

    return () => window.clearTimeout(handle);
  }, [canManageStaff, inputValue, isDialogOpen]);

  useEffect(() => {
    if (!canManageStaff || !org_path_name || !isDialogOpen) return;
    if (!debouncedQuery) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      setIsSearching(true);
      setSearchError(null);

      try {
        const options = {
          endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/staff/search?query=${encodeURIComponent(
            debouncedQuery,
          )}`,
          method: 'GET',
          signal: controller.signal,
        } as RequestOptions & { signal?: AbortSignal };

        const response = await apiFetchRequest(options);
        if (!response) {
          setSearchError('Unable to search right now. Please try again.');
          setSearchResults([]);
          setHasSearched(false);
          return;
        }

        const body = response.body as {
          message?: string;
          results?: StaffSearchResult[];
          detail?: string;
          status_code?: number;
        };

        if (
          response.status === 401 ||
          body?.status_code === 401 ||
          body?.message === 'UNAUTHENTICATED' ||
          body?.message === 'NO_ACCESS'
        ) {
          navigate('/401.html');
          return;
        }

        if (response.status === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND') {
          navigate('/404.html');
          return;
        }

        if (response.isError || body?.message !== 'SUCCESS' || !Array.isArray(body.results)) {
          const detail = (body?.detail as string) || 'Unable to find matching users.';
          setSearchError(detail);
          setSearchResults([]);
          setHasSearched(false);
          return;
        }

        setSearchResults(body.results);
        setHasSearched(true);
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          setSearchError('Unable to search right now. Please try again.');
          setSearchResults([]);
          setHasSearched(false);
        }
      } finally {
        setIsSearching(false);
      }
    })();

    return () => controller.abort();
  }, [apiFetchRequest, canManageStaff, debouncedQuery, isDialogOpen, navigate, org_path_name]);

  const handleConfirmAddStaff = async () => {
    if (!org_path_name) return;

    if (!selectedCandidate?.user_id) {
      setDialogMessage({ type: 'error', text: 'Select a user to add to the organization.' });
      return;
    }

    if (!isEmailDomainAllowed(selectedCandidate.email ?? null)) {
      setDialogMessage({
        type: 'error',
        text: invalidEmailText,
      });
      return;
    }

    if (!selectedRole) {
      setDialogMessage({ type: 'error', text: 'Select a role for the new staff member.' });
      return;
    }

    setIsAddingStaff(true);
    setDialogMessage(null);

    let shouldCloseDialog = false;

    try {
      const options: RequestOptions = {
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/staff/`,
        method: 'POST',
        body: { user_id: selectedCandidate.user_id, role: selectedRole },
      };

      const response = await apiFetchRequest(options);
      if (!response) {
        setDialogMessage({ type: 'error', text: 'Unable to add staff member.' });
        return;
      }

      const body = response.body as { message?: string; detail?: string; status_code?: number };

      if (
        response.status === 401 ||
        body?.status_code === 401 ||
        body?.message === 'UNAUTHENTICATED' ||
        body?.message === 'NO_ACCESS'
      ) {
        navigate('/401.html');
        return;
      }

      if (response.status === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND') {
        navigate('/404.html');
        return;
      }

      if (response.isError || body?.message !== 'SUCCESS') {
        const detail = (body?.detail as string) || 'Unable to add staff member.';
        setDialogMessage({ type: 'error', text: detail });
        return;
      }

      setActionMessage({ type: 'success', text: 'Staff member added successfully.' });
      await fetchOrganizationStaff();
      shouldCloseDialog = true;
    } catch {
      setDialogMessage({ type: 'error', text: 'Unable to add staff member. Please try again.' });
    } finally {
      setIsAddingStaff(false);
      if (shouldCloseDialog) handleCloseDialog();
    }
  };

  const handleSendInvite = async () => {
    if (!org_path_name) return;

    const trimmed = inputValue.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setDialogMessage({ type: 'error', text: 'Enter a valid email address to send an invite.' });
      return;
    }
    if (!isEmailDomainAllowed(trimmed)) {
      setDialogMessage({ type: 'error', text: invalidEmailText });
      return;
    }

    setIsInviting(true);
    setDialogMessage(null);
    let shouldCloseDialog = false;

    try {
      const inviteRole = (selectedRole || 'member').toLowerCase();
      const options: RequestOptions = {
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/staff/invite`,
        method: 'POST',
        body: { email: trimmed, role: inviteRole },
      };

      const response = await apiFetchRequest(options);
      if (!response) {
        setDialogMessage({ type: 'error', text: 'Unable to send invite.' });
        return;
      }

      const body = response.body as { message?: string; detail?: string; status_code?: number };

      if (
        response.status === 401 ||
        body?.status_code === 401 ||
        body?.message === 'UNAUTHENTICATED' ||
        body?.message === 'NO_ACCESS'
      ) {
        navigate('/401.html');
        return;
      }

      if (response.status === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND') {
        navigate('/404.html');
        return;
      }

      if (response.isError || body?.message !== 'SUCCESS') {
        const detail = (body?.detail as string) || 'Unable to send invite.';
        setDialogMessage({ type: 'error', text: detail });
        return;
      }

      setActionMessage({ type: 'success', text: 'Invite sent successfully.' });
      shouldCloseDialog = true;
    } catch {
      setDialogMessage({ type: 'error', text: 'Unable to send invite right now. Please try again.' });
    } finally {
      setIsInviting(false);
      if (shouldCloseDialog) handleCloseDialog();
    }
  };

  const handleRoleChange = (event: SelectChangeEvent<string>) => {
    setSelectedRole(event.target.value);
  };

  if ((isLoading && !organization && !errorText) || !org_path_name) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100vh' }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  if (errorText) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', px: 2 }}>
        <Typography color="error">{errorText}</Typography>
      </Box>
    );
  }

  if (!organization) return <></>;

  const searchDropdownOpen =
    isDialogOpen && !!debouncedQuery && !isSearching && searchResults.length > 0 && !selectedCandidate;

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'transparent',
          padding: '0px 40px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 4,
          flexWrap: 'wrap',
          mt: 0,
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Staff
          </Typography>
        </Box>
        {canManageStaff && staff.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <BorderedButton onClick={handleOpenDialog} buttonText="Add Staff" {...addStaffButtonProps} />
          </Box>
        )}
      </Box>

      {actionMessage && (
        <Box sx={{ px: '40px', mb: 4 }}>
          <Alert severity={actionMessage.type} onClose={() => setActionMessage(null)}>
            {actionMessage.text}
          </Alert>
        </Box>
      )}

      <Box
        sx={{ p: 2 }}
        style={{
          backgroundColor: theme.layout.defaults.backgroundColor,
          borderRadius: '16px',
          padding: '40px',
          width: '100%',
        }}
      >
        <Box>
          {staff.length ? (
            <StaffTable<OrganizationStaffMember>
              title=""
              staff={staff}
              getRowKey={(m, i) => m.org_staff_id ?? m.user_id ?? `org-staff-${i}`}
              renderActions={undefined}
            />
          ) : (
            <Box
              sx={{
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" sx={{ mb: canManageStaff ? 2 : 0 }}>
                No staff members assigned to this organization.
              </Typography>
              {canManageStaff && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <BorderedButton onClick={handleOpenDialog} buttonText="Add Staff" {...addStaffButtonProps} />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <AddStaffModal
        open={isDialogOpen}
        searchResults={searchResults}
        onClose={handleCloseDialog}
        onSearchInputChange={(value) => {
          setInputValue(value);
          setSearchError(null);
          setDialogMessage(null);
        }}
        onCandidateChange={(candidate) => {
          setSelectedCandidate(candidate);
          setDialogMessage(null);
        }}
        onRoleChange={(event) => {
          handleRoleChange(event);
          setDialogMessage(null);
        }}
        onAddStaff={handleConfirmAddStaff}
        onSendInvite={handleSendInvite}
        inputValue={inputValue}
        isSearching={isSearching}
        hasSearched={hasSearched}
        searchError={searchError}
        dialogMessage={dialogMessage}
        roles={roles}
        selectedRole={selectedRole}
        isRolesLoading={isRolesLoading}
        rolesError={rolesError}
        isAddingStaff={isAddingStaff}
        isInviting={isInviting}
        selectedCandidate={selectedCandidate}
        searchDropdownOpen={searchDropdownOpen}
      />
    </>
  );
}
